import AccountBalanceWalletRounded from '@mui/icons-material/AccountBalanceWalletRounded';
import CallSplitRounded from '@mui/icons-material/CallSplitRounded';
import Inventory2Rounded from '@mui/icons-material/Inventory2Rounded';
import SavingsRounded from '@mui/icons-material/SavingsRounded';
import { Box, Stack, Typography } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useNotify } from '../../../lib/notify';
import { useSaveSettings, type ShopSettings } from '../api';
import { ChoiceCards, SaveBar, SectionCard, SettingSwitch } from '../ui';

interface Values {
  allowNegativeStock: boolean;
  excessPaymentPolicy: ShopSettings['excessPaymentPolicy'];
  autoApplyCredit: boolean;
}

function toValues(s: ShopSettings): Values {
  return {
    allowNegativeStock: s.allowNegativeStock,
    excessPaymentPolicy: s.excessPaymentPolicy,
    autoApplyCredit: s.autoApplyCredit,
  };
}

export function PoliciesTab({
  settings,
  canManage,
}: {
  settings: ShopSettings;
  canManage: boolean;
}) {
  const notify = useNotify();
  const save = useSaveSettings();
  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<Values>({ values: toValues(settings) });
  const ro = !canManage;

  const onSubmit = handleSubmit(async (v) => {
    try {
      await save.mutateAsync(v);
      notify.success('Sales & stock policies saved');
    } catch (e) {
      notify.error(e);
    }
  });

  return (
    <form onSubmit={(e) => void onSubmit(e)} noValidate>
      <Stack spacing={2.5}>
        <SectionCard
          icon={<Inventory2Rounded />}
          title="Stock"
          description="What happens when a sale needs more than you have on the shelf."
        >
          <Controller
            control={control}
            name="allowNegativeStock"
            render={({ field }) => (
              <SettingSwitch
                title="Allow selling below zero stock"
                description={
                  field.value
                    ? 'Sales go through even when stock runs out; stock shows as negative until you receive more.'
                    : 'Sales are blocked when there isn’t enough stock. Recommended for accurate stock.'
                }
                checked={field.value}
                onChange={field.onChange}
                disabled={ro}
              />
            )}
          />
        </SectionCard>

        <SectionCard
          icon={<AccountBalanceWalletRounded />}
          title="Payments & credit"
          description="How customer payments are matched to what they owe."
        >
          <Typography variant="subtitle2" sx={{ mb: 1.25 }}>
            When a customer pays more than the invoice
          </Typography>
          <Controller
            control={control}
            name="excessPaymentPolicy"
            render={({ field }) => (
              <ChoiceCards
                label="Excess payment policy"
                value={field.value}
                onChange={field.onChange}
                disabled={ro}
                options={[
                  {
                    value: 'KEEP_AS_CREDIT',
                    title: 'Keep as credit',
                    description: 'The extra stays on their account as an advance.',
                    icon: <SavingsRounded />,
                  },
                  {
                    value: 'APPLY_FIFO',
                    title: 'Settle older invoices',
                    description: 'The extra pays off their oldest open invoices first.',
                    icon: <CallSplitRounded />,
                  },
                ]}
              />
            )}
          />
          <Box sx={{ mt: 1.5 }}>
            <Controller
              control={control}
              name="autoApplyCredit"
              render={({ field }) => (
                <SettingSwitch
                  title="Use available credit automatically"
                  description="When a customer with credit buys again, their credit is applied to the new invoice."
                  checked={field.value}
                  onChange={field.onChange}
                  disabled={ro}
                />
              )}
            />
          </Box>
        </SectionCard>
      </Stack>
      <SaveBar
        dirty={isDirty}
        saving={save.isPending}
        disabled={ro}
        onReset={() => reset(toValues(settings))}
      />
    </form>
  );
}
